import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { cartAPI, wishlistAPI } from '../services/api';

const Ctx = createContext(null);
const isAuthError = (e) => e?.response?.status === 401 || e?.response?.status === 403;

const parseJwtPayload = (token) => {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const getTokenRole = () => {
  try {
    const token = localStorage.getItem('glovy_token');
    if (!token) return '';
    const payload = parseJwtPayload(token);
    return String(payload?.role || '').toUpperCase();
  } catch {
    return '';
  }
};

const isTokenExpired = (token) => {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec;
};

const normalizeUser = (u) => {
  if (!u) return null;
  const uid = u.userId ?? u.id;
  const role = String(u.role || '').toUpperCase() || getTokenRole();
  return uid ? { ...u, userId: uid, role } : { ...u, role };
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const token = localStorage.getItem('glovy_token');
      if (token && isTokenExpired(token)) {
        localStorage.removeItem('glovy_token');
        localStorage.removeItem('glovy_user');
        return null;
      }
      const saved = normalizeUser(JSON.parse(localStorage.getItem('glovy_user')));
      if (!token && saved) {
        localStorage.removeItem('glovy_user');
        return null;
      }
      return saved;
    } catch {
      return null;
    }
  });
  const [cart, setCart] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const initialFetchKeyRef = useRef('');

  const login = u => {
    const nu = normalizeUser(u);
    localStorage.setItem('glovy_token', nu.token);
    localStorage.setItem('glovy_user', JSON.stringify(nu));
    setUser(nu);
  };
  const logout = () => { localStorage.removeItem('glovy_token'); localStorage.removeItem('glovy_user'); setUser(null); setCart(null); setWishlist([]); };

  const uid = user?.userId ?? user?.id;
  const hasToken = () => {
    const token = localStorage.getItem('glovy_token');
    if (!token) return false;
    if (isTokenExpired(token)) {
      localStorage.removeItem('glovy_token');
      localStorage.removeItem('glovy_user');
      return false;
    }
    return true;
  };

  const fetchCart = useCallback(async () => {
    if (!uid || !hasToken()) return;
    try {
      setCart(await cartAPI.get(uid));
    } catch (e) {
      if (isAuthError(e)) logout();
    }
  }, [uid]);

  const fetchWishlist = useCallback(async () => {
    if (!uid || !hasToken()) return;
    try {
      setWishlist(await wishlistAPI.get(uid));
    } catch (e) {
      if (isAuthError(e)) logout();
    }
  }, [uid]);

  useEffect(() => {
    if (!uid || !hasToken()) return;
    const token = localStorage.getItem('glovy_token') || '';
    const key = `${uid}:${token}`;
    if (initialFetchKeyRef.current === key) return;
    initialFetchKeyRef.current = key;
    fetchCart();
    fetchWishlist();
  }, [uid, fetchCart, fetchWishlist]);

  useEffect(() => {
    if (user && !localStorage.getItem('glovy_token')) {
      logout();
    }
  }, [user]);

  useEffect(() => {
    const onAuthCleared = () => logout();
    window.addEventListener('glovy-auth-cleared', onAuthCleared);
    return () => window.removeEventListener('glovy-auth-cleared', onAuthCleared);
  }, []);

  const addToCart = async (pid, qty = 1) => {
    if (!uid || !hasToken()) return { ok: false, error: 'Session expired. Please sign in again.' };
    try {
      setCart(await cartAPI.add(uid, pid, qty));
      setCartOpen(true);
      return { ok: true };
    } catch (e) {
      if (isAuthError(e)) logout();
      return { ok: false, error: e?.response?.data?.error || 'Could not add this item' };
    }
  };
  const removeFromCart = async iid => {
    if (!uid || !hasToken()) return;
    try {
      setCart(await cartAPI.remove(uid, iid));
    } catch (e) {
      if (isAuthError(e)) logout();
    }
  };
  const updateCartQty  = async (iid, q) => {
    if (!uid || !hasToken()) return;
    try {
      setCart(await cartAPI.updateQty(uid, iid, q));
    } catch (e) {
      if (isAuthError(e)) logout();
    }
  };
  const clearCart      = async () => {
    if (!uid || !hasToken()) return;
    try {
      setCart(await cartAPI.clear(uid));
    } catch (e) {
      if (isAuthError(e)) logout();
    }
  };

  const toggleWishlist = async pid => {
    if (!uid || !hasToken()) return false;
    const inList = wishlist.some(w => w.product?.id === pid);
    try {
      if (inList) { await wishlistAPI.remove(uid, pid); setWishlist(w => w.filter(x => x.product?.id !== pid)); }
      else { await wishlistAPI.add(uid, pid); await fetchWishlist(); }
      return true;
    } catch (e) {
      if (isAuthError(e)) logout();
      return false;
    }
  };

  const isInWishlist = pid => wishlist.some(w => w.product?.id === pid);
  const cartCount = cart?.items?.reduce((s, i) => s + i.quantity, 0) || 0;

  return (
    <Ctx.Provider value={{ user, login, logout, cart, cartOpen, setCartOpen, cartCount, addToCart, removeFromCart, updateCartQty, clearCart, fetchCart, wishlist, toggleWishlist, isInWishlist, fetchWishlist }}>
      {children}
    </Ctx.Provider>
  );
}
export const useApp = () => useContext(Ctx);
